(function () {
  'use strict';

  if (window.UnifiedPerformance) {
    return;
  }

  const VERSION = '1.0.0';
  const MAX_HISTORY = 200;

  const active = new Map();
  const history = [];

  let sequence = 0;

  /*
   * ------------------------------------------------------------
   * HELPERS
   * ------------------------------------------------------------
   */

  function getConfig() {
    return (
      window.UnifiedTrackingConfig ||
      {}
    );
  }

  function isDebugEnabled() {
    return (
      getConfig().debug === true
    );
  }

  function now() {
    if (
      window.performance &&
      typeof window.performance.now ===
        'function'
    ) {
      return window.performance.now();
    }

    return Date.now();
  }

  function nowIso() {
    return new Date()
      .toISOString();
  }

  function clone(value) {
    try {
      return JSON.parse(
        JSON.stringify(value)
      );
    } catch (error) {
      return value;
    }
  }

  function sanitizeMeta(meta) {
    if (
      !meta ||
      typeof meta !== 'object' ||
      Array.isArray(meta)
    ) {
      return {};
    }

    /*
     * Operational metadata only.
     * Payloads and arbitrary nested objects
     * are deliberately excluded.
     */

    const allowedKeys = [
      'event',
      'status',
      'reason',
      'provider',
      'decision',
      'changed',
      'changes',
      'aliases',
      'warnings',
      'errors',
      'matched_rules',
      'routes'
    ];

    const result = {};

    allowedKeys.forEach(
      function (key) {
        if (
          !Object.prototype
            .hasOwnProperty.call(
              meta,
              key
            )
        ) {
          return;
        }

        const value =
          meta[key];

        if (
          typeof value ===
            'string'
        ) {
          result[key] =
            value.slice(
              0,
              120
            );

          return;
        }

        if (
          typeof value ===
            'number' ||
          typeof value ===
            'boolean'
        ) {
          result[key] =
            value;

          return;
        }

        if (
          Array.isArray(value)
        ) {
          result[key] =
            value
              .filter(
                function (item) {
                  return (
                    typeof item ===
                      'string' ||
                    typeof item ===
                      'number' ||
                    typeof item ===
                      'boolean'
                  );
                }
              )
              .slice(
                0,
                20
              );
        }
      }
    );

    return result;
  }

  /*
   * ------------------------------------------------------------
   * BUS
   * ------------------------------------------------------------
   */

  function emit(
    type,
    data
  ) {
    if (
      window.UnifiedBus &&
      typeof window.UnifiedBus.emit ===
        'function'
    ) {
      try {
        return window.UnifiedBus.emit(
          type,
          data || {}
        );
      } catch (error) {
        // Legacy fallback below.
      }
    }

    try {
      window.dispatchEvent(
        new CustomEvent(
          'unified:debug',
          {
            detail: {
              type:
                type,

              timestamp:
                nowIso(),

              data:
                data || {}
            }
          }
        )
      );
    } catch (error) {
      // Best effort only.
    }

    return null;
  }

  /*
   * ------------------------------------------------------------
   * HISTORY
   * ------------------------------------------------------------
   */

  function save(entry) {
    history.push(
      entry
    );

    if (
      history.length >
      MAX_HISTORY
    ) {
      history.splice(
        0,
        history.length -
          MAX_HISTORY
      );
    }
  }

  /*
   * ------------------------------------------------------------
   * START
   * ------------------------------------------------------------
   */

  function start(
    label,
    meta
  ) {
    const normalizedLabel =
      String(
        label || ''
      )
        .trim()
        .toLowerCase();

    if (!normalizedLabel) {
      return null;
    }

    sequence += 1;

    const token = {
      id:
        sequence,

      label:
        normalizedLabel,

      started_at:
        now(),

      timestamp:
        nowIso(),

      meta:
        sanitizeMeta(
          meta
        )
    };

    active.set(
      token.id,
      token
    );

    return token.id;
  }

  /*
   * ------------------------------------------------------------
   * END
   * ------------------------------------------------------------
   */

  function end(
    tokenId,
    meta
  ) {
    if (
      !active.has(
        tokenId
      )
    ) {
      return null;
    }

    const token =
      active.get(
        tokenId
      );

    active.delete(
      tokenId
    );

    const duration =
      Math.max(
        0,
        now() -
          token.started_at
      );

    const mergedMeta =
      Object.assign(
        {},
        token.meta,
        sanitizeMeta(
          meta
        )
      );

    const entry = {
      id:
        token.id,

      label:
        token.label,

      timestamp:
        token.timestamp,

      completed_at:
        nowIso(),

      duration_ms:
        Number(
          duration.toFixed(3)
        ),

      meta:
        mergedMeta
    };

    save(
      entry
    );

    emit(
      'performance_measurement',
      {
        label:
          entry.label,

        duration_ms:
          entry.duration_ms,

        event:
          entry.meta.event ||
          '',

        status:
          entry.meta.status ||
          ''
      }
    );

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Performance]',
        entry.label,
        entry.duration_ms +
          ' ms',
        clone(
          entry.meta
        )
      );
    }

    return clone(
      entry
    );
  }

  /*
   * ------------------------------------------------------------
   * ONE-SHOT MEASUREMENT
   * ------------------------------------------------------------
   */

  function measure(
    label,
    callback,
    meta
  ) {
    if (
      typeof callback !==
        'function'
    ) {
      return null;
    }

    const tokenId =
      start(
        label,
        meta
      );

    try {
      const result =
        callback();

      end(
        tokenId,
        {
          status:
            'success'
        }
      );

      return result;
    } catch (error) {
      end(
        tokenId,
        {
          status:
            'error',

          reason:
            error.message
        }
      );

      throw error;
    }
  }

  /*
   * ------------------------------------------------------------
   * QUERY API
   * ------------------------------------------------------------
   */

  function getHistory() {
    return clone(
      history
    );
  }

  function getActive() {
    return Array.from(
      active.values()
    ).map(
      function (token) {
        return {
          id:
            token.id,

          label:
            token.label,

          timestamp:
            token.timestamp,

          running_ms:
            Number(
              (
                now() -
                token.started_at
              ).toFixed(3)
            ),

          meta:
            clone(
              token.meta
            )
        };
      }
    );
  }

  function findByLabel(
    label
  ) {
    const normalized =
      String(
        label || ''
      )
        .trim()
        .toLowerCase();

    return getHistory()
      .filter(
        function (entry) {
          return (
            entry.label ===
            normalized
          );
        }
      );
  }

  function getStats() {
    const grouped = {};

    history.forEach(
      function (entry) {
        if (
          !grouped[
            entry.label
          ]
        ) {
          grouped[
            entry.label
          ] = {
            count: 0,
            total_ms: 0,
            min_ms: null,
            max_ms: null
          };
        }

        const group =
          grouped[
            entry.label
          ];

        group.count += 1;

        group.total_ms +=
          entry.duration_ms;

        group.min_ms =
          group.min_ms === null
            ? entry.duration_ms
            : Math.min(
                group.min_ms,
                entry.duration_ms
              );

        group.max_ms =
          group.max_ms === null
            ? entry.duration_ms
            : Math.max(
                group.max_ms,
                entry.duration_ms
              );
      }
    );

    Object.keys(grouped)
      .forEach(
        function (label) {
          const group =
            grouped[label];

          group.total_ms =
            Number(
              group.total_ms
                .toFixed(3)
            );

          group.average_ms =
            group.count
              ? Number(
                  (
                    group.total_ms /
                    group.count
                  ).toFixed(3)
                )
              : 0;
        }
      );

    return {
      version:
        VERSION,

      measurements:
        history.length,

      active:
        active.size,

      history_limit:
        MAX_HISTORY,

      by_label:
        grouped
    };
  }

  function printHistory() {
    console.table(
      history.map(
        function (entry) {
          return {
            id:
              entry.id,

            label:
              entry.label,

            duration_ms:
              entry.duration_ms,

            event:
              entry.meta.event ||
              '',

            status:
              entry.meta.status ||
              '',

            timestamp:
              entry.timestamp
          };
        }
      )
    );

    return getHistory();
  }

  function clear() {
    active.clear();

    history.length = 0;

    if (
      isDebugEnabled()
    ) {
      console.info(
        '[Unified Performance] History cleared.'
      );
    }

    return true;
  }

  /*
   * ------------------------------------------------------------
   * PUBLIC API
   * ------------------------------------------------------------
   */

  window.UnifiedPerformance = {
    version:
      VERSION,

    start:
      start,

    end:
      end,

    measure:
      measure,

    getHistory:
      getHistory,

    getActive:
      getActive,

    findByLabel:
      findByLabel,

    getStats:
      getStats,

    printHistory:
      printHistory,

    clear:
      clear
  };

  if (
    isDebugEnabled()
  ) {
    console.info(
      '[Unified Performance] Performance Monitor initialized',
      {
        version:
          VERSION,

        historyLimit:
          MAX_HISTORY
      }
    );
  }
})();